/**
 * PROP-E.2 — the public share surface's data access.
 *
 * READ THROUGH AN RPC, WRITE THROUGH AN ENDPOINT. The same split CB Epic 5's signing ceremony uses:
 * `get_proposal_share_context` is a SECURITY DEFINER function callable by `anon`, so an unauthenticated
 * customer gets exactly the fields the function chooses to return and nothing more — no table grant, no
 * RLS policy to widen by accident. Signing the PDF needs service_role, so that stays a backend endpoint.
 *
 * NOTHING HERE IS TENANT-SCOPED BY THE CLIENT, deliberately. The token IS the authorisation, and the
 * function resolves the tenant from it. A client-supplied tenant would be a parameter an attacker
 * controls.
 */
import { supabase } from '@/lib/supabase';
import { parseShareContext, type ProposalShareContext } from '@/lib/proposals/shareView';

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || 'https://admin-api.joulescrm.com';

/**
 * Fetch the branded viewer's context for a share token.
 *
 * ⚠️ THIS CALL HAS A SIDE EFFECT: the RPC is VOLATILE and counts the page open into
 * `view_count` / `last_viewed_at`. So it must be called ONCE per page view — a re-fetch on every render,
 * or a React 18 StrictMode double-invoke in dev, inflates the rep's view count. The page guards it with
 * a ref for exactly that reason.
 *
 * An RPC error resolves to `invalid` rather than throwing: the page is unauthenticated and driven by a
 * URL-bar token, so "we could not confirm this link" and "this link is not valid" are the same outcome
 * for the customer, and neither may render a document.
 */
export async function getProposalShareContext(token: string): Promise<ProposalShareContext> {
  try {
    const { data, error } = await supabase.rpc('get_proposal_share_context', { p_token: token });
    if (error) {
      console.error('[proposalShareService] context rpc failed', error);
      return { status: 'invalid' };
    }
    return parseShareContext(data);
  } catch (e) {
    console.error('[proposalShareService] context rpc threw', e);
    return { status: 'invalid' };
  }
}

/**
 * The document hop — a 302 to a freshly signed PDF URL, counted as a document open.
 *
 * Returned as a URL rather than fetched, so the browser follows the redirect itself: that is what lets an
 * `<iframe>` and a plain download link both work off one route, and it keeps a short-lived signed URL out
 * of this app's JavaScript entirely.
 */
export function proposalShareDocumentUrl(token: string): string {
  return `${BACKEND_URL}/api/v1/proposals/p/${encodeURIComponent(token)}/document`;
}
